import type { Contact } from '../../domain/contact';
import type { CollectedDataItem } from '../../domain/message';
import UrlService from '../UrlService';

import SharedDataExtractionService from './sharedDataExtractionService';

const createLinkedinDataExtractionService = () => {
  const { extractName, extractTitle, extractCountry, extractCompanyName, extractCompanyDomain, extractContentByKey } =
    SharedDataExtractionService;

  const extractCompanyLinkedinIds = (collectedData: CollectedDataItem[]): number[] => {
    const urls = collectedData.find((item) => item.name === 'Current Companies URLs')?.attributes?.href ?? [];
    return (urls as string[])
      .map((url) => {
        const id = UrlService.getCompanyLinkedinIdFromUrl(new URL(url));
        return parseInt(id ?? '');
      })
      .filter(Boolean);
  };

  const extractCompanyNamesFromExperience = (collectedData: CollectedDataItem[], key: string) => {
    const companyElem = collectedData.find(({ name }) => name === key);
    if (!companyElem) {
      return;
    }

    return (companyElem.content as string[]).map((name) => name?.split(' · ')[0]);
  };

  const extractContactInfo = (collectedData: CollectedDataItem[]): Partial<Contact> => {
    const name = extractName(collectedData);
    const title = extractTitle(collectedData);
    const titleFromTheList = extractContentByKey(collectedData, 'Title from the list');
    const country = extractCountry(collectedData);
    const numericCompanyLinkedinIds = extractCompanyLinkedinIds(collectedData);
    const companyName = extractCompanyName(collectedData);
    const companyNamesFromExperience =
      extractCompanyNamesFromExperience(collectedData, 'Current Companies Names') || [];
    const companyNamesFromExperienceInList =
      extractCompanyNamesFromExperience(collectedData, 'Current Companies Names from lists') || [];
    const mergedCompanyNamesList = companyNamesFromExperienceInList
      .map((company, index) => (company !== undefined ? company : companyNamesFromExperience[index]))
      .filter((company) => company !== undefined)
      .slice(0, 1);

    return {
      name,
      title: typeof titleFromTheList === 'string' && titleFromTheList.trim().length > 0 ? titleFromTheList : title,
      country,
      numericCompanyLinkedinIds,
      companyName,
      companyNames: mergedCompanyNamesList,
    };
  };

  const extractContactLinkedinId = (collectedData: CollectedDataItem[]) => {
    const linkedinElem = collectedData.find(({ name }) => name === 'Linkedin URL');
    if (!linkedinElem || !linkedinElem.attributes?.['href']) {
      return;
    }
    const url = new URL(linkedinElem.attributes['href'] as string);

    return UrlService.getContactLinkedinIdFromUrl(url);
  };

  const extractContactSearchInfo = (collectedData: CollectedDataItem[]): Partial<Contact>[] => {
    const namesData = collectedData.find((item) => item.name === 'Search names');
    const hrefs = Array.isArray(namesData?.attributes?.href) ? namesData?.attributes?.href : [];
    const names = namesData?.content ?? [];
    const positions = collectedData.find((item) => item.name === 'Search positions')?.content ?? [];
    const locations = collectedData.find((item) => item.name === 'Search locations')?.content ?? [];

    return hrefs
      .map((href, index) => {
        if (!href) {
          return;
        }
        const name = names[index] || '';
        const url = new URL(href);
        const linkedin = `${url.origin}${url.pathname}`;
        return {
          id: linkedin,
          name: name.split('\n')[0],
          title: extractPositionFromTitle(positions[index]),
          country: locations[index],
          linkedin,
          linkedinId: url ? UrlService.getContactLinkedinIdFromUrl(url) : '',
          matchedProfiles: [],
          hasDirectPhoneNumbers: false,
          hasMobilePhoneNumbers: false,
          hasValidEmails: false,
          companyName: extractContactCompanyFromTitle(positions[index]),
        };
      })
      .filter(Boolean) as Partial<Contact>[];
  };

  const extractAllSearchInfo = (collectedData: CollectedDataItem[]): Partial<Contact>[] =>
    extractContactSearchInfo(collectedData);

  const extractContactLinkedinIds = (collectedContactsSearchInfo: Partial<Contact>[] | undefined): string[] =>
    (collectedContactsSearchInfo
      ?.map((contact) => UrlService.getContactLinkedinIdFromUrl(new URL(contact.linkedin || '')))
      .filter(Boolean) as string[]) || [];

  const extractContactCompanyFromTitle = (jobTitle?: string): string => {
    if (!jobTitle) {
      return '';
    }

    const pattern = /(?:\sat\s|\s@\s|\s–\s|\sin\s)([^]+)/i;
    const match = pattern.exec(jobTitle);
    const result = (match && match[1])?.trim() || '';

    if (result) {
      return result[result.length - 1] === '.' ? result.slice(0, -1) : result;
    } else {
      return '';
    }
  };

  const extractPositionFromTitle = (jobTitle?: string): string => {
    if (!jobTitle) {
      return '';
    }

    const pattern = /^(.*?)\s(?:@|at)\s/i;
    const match = pattern.exec(jobTitle);
    if (match && match[1]) {
      return match[1].trim();
    }

    return jobTitle.trim();
  };

  return {
    extractContactInfo,
    extractCompanyDomain,
    extractContactSearchInfo,
    extractAllSearchInfo,
    extractContactLinkedinIds,
    extractContactLinkedinId,
    extractPositionFromTitle,
  };
};

const LinkedinDataExtractionService = createLinkedinDataExtractionService();

export default LinkedinDataExtractionService;
